import { Link } from 'react-router-dom';
import { type LiveChatConversation, streamStatusLabels, billingStatusLabels } from '@/mocks/superAdminLiveSupport';

interface RadioInfoPanelProps {
  conversation: LiveChatConversation | null;
}

const contractStatusConfig: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'Vigente', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  expiring: { label: 'Por vencer', color: 'text-amber-600', bg: 'bg-amber-50' },
  expired: { label: 'Vencido', color: 'text-red-600', bg: 'bg-red-50' },
};

export default function RadioInfoPanel({ conversation }: RadioInfoPanelProps) {
  if (!conversation) {
    return (
      <div className="w-full lg:w-[320px] shrink-0 bg-background-50 border border-background-200 rounded-2xl overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-background-200">
          <p className="text-foreground-800 text-xs font-bold">Información de la Radio</p>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-background-100 flex items-center justify-center">
              <i className="ri-radio-line text-foreground-300 text-lg"></i>
            </div>
            <p className="text-foreground-400 text-[10px]">Seleccioná una conversación</p>
          </div>
        </div>
      </div>
    );
  }

  const sStat = streamStatusLabels[conversation.streamStatus];
  const bStat = billingStatusLabels[conversation.billingStatus];
  const cStat = contractStatusConfig[conversation.contractStatus];

  return (
    <div className="w-full lg:w-[320px] shrink-0 bg-background-50 border border-background-200 rounded-2xl overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-background-200">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full bg-foreground-950 flex items-center justify-center shrink-0">
            <span className="text-background-50 text-[10px] font-bold">
              {conversation.radioName.slice(0, 2).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-800 text-xs font-bold truncate">{conversation.radioName}</p>
            <p className="text-foreground-400 text-[10px] truncate">{conversation.frequency}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Plan */}
        <div className="bg-background-100 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <i className="ri-vip-crown-line text-amber-500 text-xs"></i>
            </div>
            <span className="text-foreground-500 text-[10px] font-medium">Plan</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-foreground-800 text-sm font-bold">{conversation.planName}</span>
            <Link
              to="/sa/plans"
              className="text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
              title="Ver planes"
            >
              <i className="ri-external-link-line text-[10px]"></i>
            </Link>
          </div>
          <p className="text-foreground-400 text-[9px] mt-1">{conversation.billingAmount}</p>
        </div>

        {/* Frecuencia */}
        <div className="bg-background-100 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
              <i className="ri-radar-line text-sky-500 text-xs"></i>
            </div>
            <span className="text-foreground-500 text-[10px] font-medium">Frecuencia</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-foreground-800 text-sm font-bold">{conversation.frequency}</span>
            <Link
              to="/sa/frequencies"
              className="text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
              title="Ver frecuencias"
            >
              <i className="ri-external-link-line text-[10px]"></i>
            </Link>
          </div>
          <div className="flex items-center gap-2 mt-2">
            {(() => {
              const s = streamStatusLabels[conversation.streamStatus];
              return (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-semibold ${s.bg} ${s.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
                  {s.label}
                </span>
              );
            })()}
          </div>
        </div>

        {/* Contrato */}
        <div className="bg-background-100 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
              <i className="ri-file-text-line text-violet-500 text-xs"></i>
            </div>
            <span className="text-foreground-500 text-[10px] font-medium">Contrato</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-foreground-800 text-xs font-bold">{conversation.contractType}</span>
            <span className={`px-2 py-0.5 rounded-md text-[9px] font-semibold ${cStat.bg} ${cStat.color}`}>
              {cStat.label}
            </span>
          </div>
          <p className="text-foreground-400 text-[9px] mb-1.5">Vence: {conversation.contractEndDate}</p>
          <Link
            to="/sa/contracts"
            className="inline-flex items-center gap-1 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer text-[9px] font-medium"
          >
            <i className="ri-external-link-line text-[9px]"></i>
            Ver contratos
          </Link>
        </div>

        {/* Último Pago */}
        <div className="bg-background-100 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-bank-card-line text-emerald-500 text-xs"></i>
            </div>
            <span className="text-foreground-500 text-[10px] font-medium">Último Pago</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-foreground-800 text-sm font-bold">{conversation.lastPaymentAmount}</span>
            {(() => {
              const b = billingStatusLabels[conversation.billingStatus];
              return (
                <span className={`px-2 py-0.5 rounded-md text-[9px] font-semibold ${b.bg} ${b.color}`}>
                  {b.label}
                </span>
              );
            })()}
          </div>
          <p className="text-foreground-400 text-[9px]">{conversation.lastPaymentDate} · {conversation.lastPaymentMethod}</p>
          <p className="text-foreground-400 text-[9px] mt-0.5">Próximo: {conversation.billingNextDate}</p>
          <Link
            to="/sa/payments"
            className="inline-flex items-center gap-1 mt-1.5 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer text-[9px] font-medium"
          >
            <i className="ri-external-link-line text-[9px]"></i>
            Ver pagos
          </Link>
        </div>

        {/* Tickets */}
        <div className="bg-background-100 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
              <i className="ri-ticket-2-line text-rose-500 text-xs"></i>
            </div>
            <span className="text-foreground-500 text-[10px] font-medium">Tickets</span>
          </div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-foreground-800 text-2xl font-bold">{conversation.ticketCount}</span>
            <span className="text-foreground-400 text-[10px]">{conversation.ticketCount === 0 ? 'Sin tickets' : conversation.ticketCount === 1 ? 'abierto' : 'abiertos'}</span>
          </div>
          <Link
            to="/sa/support"
            className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-foreground-950 text-background-50 text-[10px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-ticket-2-line text-xs"></i>
            Ver tickets
          </Link>
        </div>

        {/* Contacto */}
        <div className="bg-background-100 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-foreground-100 flex items-center justify-center shrink-0">
              <i className="ri-contacts-line text-foreground-500 text-xs"></i>
            </div>
            <span className="text-foreground-500 text-[10px] font-medium">Contacto</span>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-foreground-600 text-[10px]">
              <i className="ri-mail-line text-foreground-400 text-[10px]"></i>
              <span className="truncate">{conversation.contactEmail}</span>
            </div>
            <div className="flex items-center gap-2 text-foreground-600 text-[10px]">
              <i className="ri-phone-line text-foreground-400 text-[10px]"></i>
              <span>{conversation.contactPhone}</span>
            </div>
            <div className="flex items-center gap-2 text-foreground-600 text-[10px]">
              <i className="ri-map-pin-line text-foreground-400 text-[10px]"></i>
              <span>{conversation.country}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}